import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { creditService } from '../services/creditService'

const CreditTransaction = () => {
  const [organisations, setOrganisations] = useState([])
  const [selectedOrganisationId, setSelectedOrganisationId] = useState('')
  const [transactions, setTransactions] = useState([])
  const [transactionSummary, setTransactionSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
    hasMore: false
  })

  useEffect(() => {
    fetchOrganisations()
  }, [])

  const fetchOrganisations = async () => {
    try {
      const response = await creditService.getOrganisations()
      setOrganisations(response.data || [])
    } catch (error) {
      console.error('Error fetching organisations:', error)
    }
  }

  const fetchTransactions = async (organisationId, offset = 0) => {
    if (!organisationId) return

    try {
      setLoading(true)
      const response = await creditService.getCreditTransactions(organisationId, {
        limit: pagination.limit,
        offset
      })
      
      if (offset === 0) {
        setTransactions(response.data.transactions || [])
      } else {
        setTransactions(prev => [...prev, ...(response.data.transactions || [])])
      }
      
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination,
        offset
      }))
    } catch (error) {
      console.error('Error fetching transactions:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchTransactionSummary = async (organisationId) => {
    if (!organisationId) return

    try {
      const response = await creditService.getTransactionSummary(organisationId)
      setTransactionSummary(response.data)
    } catch (error) {
      console.error('Error fetching transaction summary:', error)
    }
  }

  const handleOrganisationChange = (organisationId) => {
    setSelectedOrganisationId(organisationId)
    if (organisationId) {
      fetchTransactions(organisationId, 0)
      fetchTransactionSummary(organisationId)
    } else {
      setTransactions([])
      setTransactionSummary(null)
      setPagination({
        limit: 20,
        offset: 0,
        total: 0,
        hasMore: false
      })
    }
  }

  const loadMoreTransactions = () => {
    if (pagination.hasMore && !loading) {
      fetchTransactions(selectedOrganisationId, pagination.offset + pagination.limit)
    }
  }

  const formatTransactionType = (type) => {
    switch (type) {
      case 'C': return 'Credit'
      case 'D': return 'Debit'
      case 'R': return 'Refund'
      default: return type || '—'
    }
  }

  const formatTransactionTypeBadge = (type) => {
    switch (type) {
      case 'C': return 'bg-green-100 text-green-800'
      case 'D': return 'bg-red-100 text-red-800'
      case 'R': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const usageTypeLabel = (usageType) => {
    switch (usageType) {
      case 'WORKFLOW': return 'Lead Discovery'
      case 'ORGANISATION_SUMMARY': return 'Org Summary'
      case 'LEAD_SUMMARY': return 'Lead Summary'
      case 'CAMPAIGN_EMAIL': return 'Campaign Email'
      case 'CAMPAIGN': return 'Campaign'
      default: return null
    }
  }

  const usageTypeBadgeClass = (usageType) => {
    switch (usageType) {
      case 'WORKFLOW': return 'bg-blue-50 text-blue-700'
      case 'ORGANISATION_SUMMARY': return 'bg-amber-50 text-amber-700'
      case 'LEAD_SUMMARY': return 'bg-teal-50 text-teal-700'
      case 'CAMPAIGN_EMAIL': return 'bg-pink-50 text-pink-700'
      case 'CAMPAIGN': return 'bg-indigo-50 text-indigo-700'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const formatAmount = (amount) => {
    return `${parseFloat(amount).toFixed(2)}C`
  }

  const formatDollarAmount = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  return (
    <div>
      <div className="card mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Organisation
          </label>
          <select
            value={selectedOrganisationId}
            onChange={(e) => handleOrganisationChange(e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Choose an organisation...</option>
            {organisations.map((org) => (
              <option key={org.organisation_id} value={org.organisation_id}>
                {org.organisation_name} {org.website && `(${org.website})`}
              </option>
            ))}
          </select>
        </div>

        {/* Transaction Summary */}
        {transactionSummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-600">Current Balance</div>
              <div className="text-2xl font-bold text-blue-900">
                {formatAmount(transactionSummary.current_balance)}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-green-600">Total Credits</div>
              <div className="text-2xl font-bold text-green-900">
                {formatAmount(transactionSummary.total_credits)}
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-red-600">Total Debits</div>
              <div className="text-2xl font-bold text-red-900">
                {formatAmount(transactionSummary.total_debits)}
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-purple-600">Net Credits</div>
              <div className="text-2xl font-bold text-purple-900">
                {formatAmount(transactionSummary.net_credits)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Transactions Table */}
      {selectedOrganisationId && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Credit Transactions
              {selectedOrganisationId && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  ({pagination.total} total)
                </span>
              )}
            </h2>
          </div>
          
          {loading && transactions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No transactions found for this organisation
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dollar Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction.transaction_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${formatTransactionTypeBadge(transaction.transaction_type)}`}>
                          {formatTransactionType(transaction.transaction_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {usageTypeLabel(transaction.usage_type) ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${usageTypeBadgeClass(transaction.usage_type)}`}>
                            {usageTypeLabel(transaction.usage_type)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm space-y-1">
                          {transaction.model_name && (
                            <p className="text-gray-900">
                              <span className="font-medium text-gray-500">Model:</span> {transaction.model_name}
                            </p>
                          )}
                          {transaction.workflow_id && (
                            <p className="text-gray-600">
                              <span className="font-medium text-gray-500">Workflow:</span>{' '}
                              <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                                {transaction.workflow_id.substring(0, 8)}...
                              </span>
                              {transaction.workflow_status && (
                                <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                  transaction.workflow_status === 'COMPLETED'
                                    ? 'bg-green-100 text-green-800'
                                    : transaction.workflow_status === 'FAILED'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {transaction.workflow_status}
                                </span>
                              )}
                            </p>
                          )}
                          {transaction.campaign_name && (
                            <p className="text-gray-600">
                              <span className="font-medium text-gray-500">Campaign:</span> {transaction.campaign_name}
                            </p>
                          )}
                          {!transaction.model_name && !transaction.workflow_id && !transaction.campaign_id && !transaction.usage_type && (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${
                          transaction.transaction_type === 'C' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.transaction_type === 'D' ? '-' : '+'}{formatAmount(transaction.credit_amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {transaction.dollar_amount ? formatDollarAmount(transaction.dollar_amount) : '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(transaction.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Load More Button */}
          {pagination.hasMore && (
            <div className="px-6 py-4 border-t border-gray-200 text-center">
              <button
                onClick={loadMoreTransactions}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Organisation Selected Message */}
      {!selectedOrganisationId && (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No Organisation Selected</h3>
            <p className="mt-1 text-sm text-gray-500">Please select an organisation to view its credit transactions.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreditTransaction
